import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';

export type SegmentedControlOption<T = string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
};

type SegmentedControlProps<T = string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  disabled?: boolean;
  showDividers?: boolean;
};

export const SegmentedControl = <T extends string | number>({
  value,
  onChange,
  options,
  disabled = false,
  showDividers = false,
}: SegmentedControlProps<T>) => {
  return (
    <div
      className={cs(
        'tw-inline-flex tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white',
        {
          'tw-cursor-not-allowed tw-opacity-50': disabled,
        }
      )}
    >
      {options.map((option, index) => {
        const isSelected = value === option.value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        const prevOption = index > 0 ? options[index - 1] : null;
        const isPrevSelected = prevOption ? value === prevOption.value : false;

        return (
          <div
            key={String(option.value)}
            className={cs({
              'tw-border-l tw-border-neutral-200':
                showDividers && !isFirst && !isPrevSelected,
              'tw-border-l tw-border-primary-500':
                showDividers && !isFirst && isPrevSelected,
            })}
          >
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onChange(option.value);
                }
              }}
              className={cs(
                '!tw-m-[-1px]',
                'tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-px-3.5 tw-py-1 tw-text-sm tw-font-medium tw-transition-all tw-duration-150',
                {
                  'tw-rounded-l-md': isFirst,
                  'tw-rounded-r-md': isLast,
                  'tw-cursor-pointer': !disabled,
                  'tw-cursor-not-allowed': disabled,
                  // Selected state
                  'tw-border tw-border-primary-500 tw-bg-primary-50 tw-text-primary-600':
                    isSelected,
                  // Unselected state
                  'tw-border tw-border-transparent tw-bg-transparent tw-text-neutral-700 hover:tw-text-primary-700':
                    !isSelected && !disabled,
                }
              )}
            >
              {option.icon && (
                <span
                  className={cs('tw-flex tw-items-center', {
                    'tw-text-primary-600': isSelected,
                    'tw-text-neutral-500': !isSelected,
                  })}
                >
                  {option.icon}
                </span>
              )}
              <span>{option.label}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};
